import { Users, Copy, Check, Trophy, ArrowUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAccount } from 'wagmi';
import { useReferral } from '../hooks/useReferral';
import { getUserReferralStats, getTopReferrers } from '../services/supabase';
import { useAppKit } from '@reown/appkit/react';

interface TopReferrer {
  referrer_address: string;
  total_referrals: number;
  total_rewards: number;
  currency?: string;
}

export default function Referral() {
  const [copied, setCopied] = useState(false);
  const [userReferrals, setUserReferrals] = useState(0);
  const [userEarnings, setUserEarnings] = useState(0);
  const [topReferrers, setTopReferrers] = useState<TopReferrer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState('USD');
  
  // Wallet connections
  const { address: evmAddress } = useAccount();
  const { publicKey: solanaPublicKey } = useWallet();
  const { open: openConnectModal } = useAppKit();
  
  // Referral hook
  const { generateReferralLink } = useReferral();
  
  // Get user wallet address (EVM or Solana)
  const userAddress = evmAddress?.toString() || solanaPublicKey?.toBase58() || '';
  
  // Generate referral link for the connected wallet
  const referralLink = userAddress ? generateReferralLink(userAddress) : "Connect wallet to get your referral link";

  // Load referral data
  useEffect(() => {
    const loadReferralData = async () => {
      if (!userAddress) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        // Get user's referral stats
        const stats = await getUserReferralStats(userAddress);
        setUserReferrals(stats.total_referrals);
        setUserEarnings(stats.total_rewards);
        setCurrency(stats.currency);
        
        // Get top referrers
        const top = await getTopReferrers(10);
        setTopReferrers(top);
      } catch (error) {
        // Silent fail - don't log to console
      } finally {
        setIsLoading(false);
      }
    };
    
    loadReferralData();
  }, [userAddress]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Silent fail - don't log to console
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const handleConnectWallet = () => {
    if (openConnectModal) {
      openConnectModal();
    }
  };

  return (
    <div className="py-0 md:py-0 px-4 sm:px-6 lg:px-8 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-center mb-4">
          <h2 className="font-tokyo text-3xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent mb-2">
            Referral Program
          </h2>
          <p className="text-sm text-quantum-light/80">Invite friends and earn rewards together</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="quantum-card p-4">
              <h3 className="text-lg font-semibold text-quantum-light mb-3">Your Referral Link</h3>
              {userAddress ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="quantum-card flex-1 p-2 text-xs sm:text-sm text-quantum-light/70 truncate">
                    {referralLink}
                  </div>
                  <button 
                    onClick={handleCopy}
                    className="quantum-button py-2 px-4 flex items-center justify-center gap-1.5 min-w-[100px]"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span className="text-sm">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="text-sm">Copy</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button 
                    onClick={handleConnectWallet}
                    className="quantum-button py-2 px-4 flex items-center justify-center gap-1.5"
                  >
                    <Users className="w-4 h-4" />
                    <span className="text-sm">Connect Wallet</span>
                  </button>
                </div>
              )}
            </div>

            <div className="quantum-card p-4">
              <h3 className="text-lg font-semibold text-quantum-light mb-3">Rewards</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-quantum-light/80">Total Referrals</span>
                  <span className="text-sm text-quantum-light">
                    {isLoading ? "Loading..." : formatNumber(userReferrals)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-quantum-light/80">Total Earnings</span>
                  <span className="text-sm text-quantum-light">
                    {isLoading ? "Loading..." : `${formatNumber(userEarnings)} ${currency}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="quantum-card p-4 md:col-span-2">
              <h3 className="text-lg font-semibold text-quantum-light mb-3">How It Works</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="quantum-card p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-quantum-primary/20 flex items-center justify-center">
                      <span className="text-xs text-quantum-primary">1</span>
                    </div>
                    <h4 className="text-sm font-medium text-quantum-light">Share</h4>
                  </div>
                  <p className="text-xs text-quantum-light/70">
                    Share your unique referral link with friends
                  </p>
                </div>
                <div className="quantum-card p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-quantum-primary/20 flex items-center justify-center">
                      <span className="text-xs text-quantum-primary">2</span>
                    </div>
                    <h4 className="text-sm font-medium text-quantum-light">Invite</h4>
                  </div>
                  <p className="text-xs text-quantum-light/70">
                    Friends join using your referral link
                  </p>
                </div>
                <div className="quantum-card p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-quantum-primary/20 flex items-center justify-center">
                      <span className="text-xs text-quantum-primary">3</span>
                    </div>
                    <h4 className="text-sm font-medium text-quantum-light">Earn</h4>
                  </div>
                  <p className="text-xs text-quantum-light/70">
                    Earn 5% of their token purchase in the same currency they used (ETH, BNB, SOL, USDT, USDC)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}